import React, { useState } from "react";
import { ChevronDown, ShieldCheck } from "lucide-react";

const FAQ_ITEMS = [
 {
 q: "Why does it matter when a photo was taken?",
 a: "Every photo from a phone or camera carries hidden information about itself, the exact second it was taken, the device. Sometimes the location. At tribunal, that's the difference between \"I think I took this around March\" and \"this was taken on 14 March 2026 at 9:42am from my iPhone\". One sounds like a guess. The other sounds like the truth. TenantTrail reads that hidden info the moment you upload a photo. If your landlord later claims a photo was taken on a different date, we flag the mismatch before it catches you off guard.",
 },
 {
 q: "What's the \"audit hash\" on my letters and why should I care?",
 a: "Think of it as a tamper-proof fingerprint. The moment you send a letter through TenantTrail, we generate a short code from the letter's exact wording, the time it was sent and who. Change a single word or shift the time by even a second. The fingerprint changes completely. We stamp this code on the email and the PDF attachment, then store our own copy. Months later, if anyone claims you sent a different version, or didn't send the letter at all, we can still show the original with its matching fingerprint. It's mathematically impossible to fake.",
 },
 {
 q: "Why does TenantTrail use a BCC when I email my agent?",
 a: "It quietly puts a copy of every email you send onto your own TenantTrail chronology, without the agent seeing it. They get the email like normal. you get a perfectly preserved copy on your timeline. with the original send time, headers and any attachments intact. Better still, because the BCC goes to a private TenantTrail reply address, the moment your agent hits Reply (or Reply-All) their response also lands on your chronology automatically. No forwarding, no copy-pasting, no hoping you remember to file it later. The agent never sees this address and there's no way for them to know you've used it.",
 },
 {
 q: "How accurate are the \"delivered\" and \"opened\" badges on my sent letters?",
 a: "\"Delivered\" means your agent's mail server actually accepted the email, not bounced it, not filtered it out, not blocked it. \"Opened\" means someone opened the email in a program that loaded its content. Some email apps block that loading by default, so a missing \"opened\" tag doesn't always prove they didn't read it. But when you do see an \"opened\" badge, it's hard evidence the agent saw your message. Tribunals take this seriously when the agent claims \"I never received your email\".",
 },
 {
 q: "Can a landlord just screenshot something and claim that's the original?",
 a: "Not really. Screenshots strip out all the hidden information that makes a photo verifiable, no original date, no device, no location. PDFs created in Word can have their date changed in seconds. Real evidence, original photos with their hidden info intact, original emails with full headers, signed letters with fingerprints from a system the agent doesn't control, is far harder to fake. TenantTrail keeps every document in its original form and preserves the hidden information that screenshots destroy.",
 },
 {
 q: "What does \"tribunal-ready\" actually mean here?",
 a: "It means every piece of your evidence can be checked and proven independently. Every photo's hidden date is preserved. Every document has a fingerprint that can't be changed. Every email we send is logged, time-stamped, then stored. Your chronology can be locked once you've filed at tribunal, so dates can't be tweaked after the fact. Any one of these on its own is useful. Stacked together, they're how an independent investigator would establish what really happened. That's exactly what tribunals want to see.",
 },
 {
 q: "Could I lose my evidence if my account is deleted?",
 a: "Your evidence belongs to you. At any time, you can download an Evidence Bundle, a single PDF containing your chronology, every document, every photo, every letter, with all the fingerprints and timestamps baked in. We recommend doing this the moment you file at tribunal. The PDF stands on its own. Even if TenantTrail disappeared tomorrow, your bundle would still be valid evidence.",
 },
];

function FAQItem({ q, a, open, onToggle, idx }) {
 return (
 <div className="border-b border-[#E5E4DE] last:border-b-0" data-testid={`faq-item-${idx}`}>
 <button
 onClick={onToggle}
 className="w-full text-left flex items-start gap-3 py-5 group"
 aria-expanded={open}
 data-testid={`faq-toggle-${idx}`}
 >
 <span className="font-serif text-lg sm:text-xl text-[#0D2B52] flex-1 leading-snug pr-2">{q}</span>
 <ChevronDown
 size={20}
 strokeWidth={1.75}
 className={`text-[#1B6C62] mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
 />
 </button>
 {open && (
 <div className="pb-6 text-[15px] text-[#1A1A1A] leading-relaxed font-serif" data-testid={`faq-answer-${idx}`}>
 {a}
 </div>
 )}
 </div>
 );
}

export default function ForensicsFAQ() {
 const [openIdx, setOpenIdx] = useState(0);

 return (
 <section className="bg-[#FAF7F0] border-y border-[#E5E4DE]" data-testid="forensics-faq-section">
 <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
 <div className="text-center mb-10">
 <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-[#1B6C62]/30 mb-4">
 <ShieldCheck size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
 <span className="label-mono text-[#1B6C62]">Digital forensics</span>
 </div>
 <h2 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] mb-3">Why TenantTrail&apos;s evidence stands up</h2>
 <p className="text-[#6B7280] max-w-2xl mx-auto leading-relaxed">
 The thing most tenants lose at tribunal isn&apos;t the law. It&apos;s the timing of who sent what, when. Here&apos;s how we make sure that question never becomes a doubt.
 </p>
 </div>

 <div className="bg-white border border-[#E5E4DE] px-6 sm:px-8">
 {FAQ_ITEMS.map((it, i) => (
 <FAQItem
 key={i}
 idx={i}
 q={it.q}
 a={it.a}
 open={openIdx === i}
 onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
 />
 ))}
 </div>

 <p className="text-xs text-[#6B7280] italic font-serif text-center mt-6 max-w-2xl mx-auto leading-relaxed">
 None of this is legal advice. But at tribunal, a clean, time-stamped record of what really happened is what turns &ldquo;he said / she said&rdquo; into &ldquo;here&apos;s the evidence&rdquo;.
 </p>
 </div>
 </section>
 );
}
